
import React, { useState } from 'react';
import { ServicePackage, KolProfile, Language } from '../types';
import { TRANSLATIONS } from '../translations';
import { X, Lock, Wallet, CheckCircle, AlertCircle } from 'lucide-react';

interface ServiceBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  service: ServicePackage | null;
  kol: KolProfile | null;
  language: Language;
}

export const ServiceBookingModal: React.FC<ServiceBookingModalProps> = ({ 
    isOpen, 
    onClose, 
    onConfirm, 
    service, 
    kol, 
    language 
}) => {
  const t = TRANSLATIONS[language];
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState<'review' | 'success'>('review');

  if (!isOpen || !service || !kol) return null;

  const price = service.price;
  // Platform fee is deducted from KOL side, so Project pays exactly the listed price.
  const total = price;

  const handleConfirm = () => {
      setIsProcessing(true);
      setTimeout(() => {
          setIsProcessing(false);
          setStep('success');
          setTimeout(() => {
            onConfirm();
            setStep('review'); // Reset for next time
          }, 1500);
      }, 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        
        {step === 'review' ? (
            <>
                <div className="flex justify-between items-center p-6 border-b border-slate-700">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <Wallet className="text-indigo-400" />
                        {t.bookServiceTitle}
                    </h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Staking Notice */}
                    <div className="bg-indigo-900/20 border border-indigo-500/20 rounded-lg p-4 flex gap-3">
                        <Lock className="text-indigo-400 flex-shrink-0" size={20} />
                        <div>
                            <p className="text-sm font-bold text-indigo-300 mb-1">{t.stakingRequired}</p>
                            <p className="text-xs text-indigo-200 leading-relaxed">
                                {t.stakingDesc}
                            </p>
                        </div>
                    </div>

                    {/* Service Summary */}
                    <div className="space-y-4">
                        <h4 className="text-sm font-bold text-slate-300 uppercase tracking-wider">{t.serviceDetails}</h4>
                        <div className="flex items-center gap-3 mb-2">
                            <img src={kol.avatar} alt="" className="w-10 h-10 rounded-full border border-slate-600" />
                            <div>
                                <p className="font-bold text-white">{service.title}</p>
                                <p className="text-xs text-slate-400">by {kol.name}</p>
                            </div>
                        </div>
                    </div>

                    {/* Breakdown */}
                    <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                        <div className="flex justify-between text-sm text-slate-400 mb-2">
                            <span>Service Price</span>
                            <span className="text-white font-medium">${price.toFixed(2)}</span>
                        </div>
                        <div className="border-t border-slate-700/50 my-2"></div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-bold text-white">{t.totalToStake}</span>
                            <span className="text-xl font-bold text-emerald-400">${total.toFixed(2)} USDT</span>
                        </div>
                    </div>

                    {/* Fake Wallet Check */}
                    <div className="flex items-center justify-between text-xs text-slate-500 px-1">
                        <span>Your Balance:</span>
                        <span className="text-emerald-400 font-bold">$12,450.00 USDT</span>
                    </div>

                    <button 
                        onClick={handleConfirm}
                        disabled={isProcessing}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-70 text-white py-3.5 rounded-lg font-bold shadow-lg shadow-indigo-500/20 transition-all flex items-center justify-center gap-2"
                    >
                        {isProcessing ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                {t.stakingProcessing}
                            </>
                        ) : (
                            <>
                                <Lock size={18} /> {t.stakeAndBook}
                            </>
                        )}
                    </button>
                </div>
            </>
        ) : (
            <div className="p-10 text-center flex flex-col items-center">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mb-4 border border-emerald-500/50">
                    <CheckCircle size={32} className="text-emerald-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">{t.bookingSent}</h2>
                <p className="text-slate-400 text-sm">{t.bookingSentDesc}</p>
            </div>
        )}
      </div>
    </div>
  );
};
